import type { LadderBest, ProfileData } from '../meta/Profile';
import { QUALITY_MAX, QUALITY_MIN } from '../meta/economy';

/**
 * The difficulty ladder (Greater-Rift-style, July 2026). tier 0 = the base
 * game and every function here MUST be identity at 0 — the balance bot and
 * all determinism suites run at tier 0 and must not move. Tiers scale enemy
 * threat and loot richness, NEVER gem values: the summon bar is the run's
 * pacing clock, and scaling gem income would compress high-tier runs.
 */

/** Enemy HP: compounding ×1.15 per tier. */
export function tierHpMult(tier: number): number {
  return Math.pow(1.15, tier);
}

/** Enemy damage: linear +10% per tier. */
export function tierDamageMult(tier: number): number {
  return 1 + 0.10 * tier;
}

/**
 * Integer damage for the small-HP model (base 6 HP), min 1. Math.round gives
 * deliberate D3-style breakpoint walls: 1-damage hits stay 1 through tier 4,
 * become 2 at tier 5, 3 at tier 15; boss contact (2) becomes 3 at tier 3.
 * The Vitality meta track and HP artifacts exist to answer these walls.
 * (Tuning knob: Math.floor would move the first wall to tier 10.)
 */
export function scaleTierDamage(damage: number, tier: number): number {
  return Math.max(1, Math.round(damage * tierDamageMult(tier)));
}

/** Loot-quality floor: +1%/tier, meets the sacred 1.20 cap at tier 40. */
export function tierQualityFloor(tier: number): number {
  return Math.min(QUALITY_MAX, QUALITY_MIN + 0.01 * tier);
}

/** Run-end shard tithe multiplier: +10%/tier. */
export function tierTitheMult(tier: number): number {
  return 1 + 0.10 * tier;
}

/** Shared base for elite drop gates (SpawnDirector AND floor-event ambushes). */
export const ELITE_DROP_CHANCE_BASE = 0.6;

/** Elite drop chance: +2%/tier, guaranteed from tier 20. */
export function tierEliteDropChance(tier: number): number {
  return Math.min(1, ELITE_DROP_CHANCE_BASE + 0.02 * tier);
}

// ---------------------------------------------------------------- ladder

/** Highest tier with a recorded clear, -1 if none. */
export function highestClearedTier(data: ProfileData): number {
  let best = -1;
  for (const key of Object.keys(data.ladder.bests)) {
    const t = Number(key);
    if (Number.isInteger(t) && t > best) best = t;
  }
  return best;
}

/**
 * Open-ended unlock chain: clearing tier N unlocks N+1. The wins>0 branch
 * grandfathers pre-ladder profiles whose base-game wins predate ladder.bests.
 */
export function maxUnlockedTier(data: ProfileData): number {
  return Math.max(highestClearedTier(data) + 1, data.stats.wins > 0 ? 1 : 0);
}

/** Best recorded clear time for a tier, seconds; undefined if unrecorded. */
export function bestTime(data: ProfileData, tier: number): number | undefined {
  return data.ladder.bests[String(tier)]?.seconds;
}

/** The full record for a tier (time + character + kills); undefined if none. */
export function bestRecord(data: ProfileData, tier: number): LadderBest | undefined {
  return data.ladder.bests[String(tier)];
}

/**
 * THE player-facing name of the ladder — every UI string routes through here
 * so renaming ("Level" → something more evocative) is a one-line change.
 */
export function tierLabel(tier: number): string {
  return `Level ${tier}`;
}
